import React, { useState, useEffect } from 'react';
import { Form, Button, Space, Modal, message, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { TokenDelivery, TokenDeliveryRequest, Token } from '../../types/tokenDelivery';
import { Delivery } from '../../types/delivery';
import { tokenDeliveryService } from '../../services/tokenDeliveryService';
import { deliveryService } from '../../services/deliveryService';

interface TokenDeliveryFormProps {
  tokenDelivery?: TokenDelivery;
  isVisible: boolean;
  onClose: () => void;
  onSuccess: () => void;
  existingTokenIds: number[]; // IDs of tokens that already have connections
}

const TokenDeliveryForm: React.FC<TokenDeliveryFormProps> = ({
  tokenDelivery,
  isVisible,
  onClose,
  onSuccess,
  existingTokenIds,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [tokens, setTokens] = useState<Token[]>([]);
  const [deliveries, setDeliveries] = useState<Delivery[]>([]);
  const [loadingData, setLoadingData] = useState(false);

  const isEditing = !!tokenDelivery;
  const title = isEditing 
    ? t('tokenDeliveries.form.edit_title') 
    : t('tokenDeliveries.form.create_title');

  // Load tokens and deliveries when modal opens
  useEffect(() => {
    if (isVisible) {
      loadData();
    }
  }, [isVisible]);

  const loadData = async () => {
    setLoadingData(true);
    try {
      const [tokensData, deliveriesData] = await Promise.all([
        tokenDeliveryService.getAllTokens(),
        deliveryService.getAllDeliveries(),
      ]);
      setTokens(tokensData);
      setDeliveries(deliveriesData);
    } catch (error) {
      console.error('Error loading data:', error);
      message.error(t('tokenDeliveries.notifications.error', { message: (error as Error).message }));
    } finally {
      setLoadingData(false);
    }
  };

  const handleSubmit = async (values: { token_id: number; delivery_id: number }) => {
    setIsLoading(true);
    try {
      if (isEditing && tokenDelivery) {
        await tokenDeliveryService.updateTokenDelivery(tokenDelivery.id, {
          delivery_id: values.delivery_id,
        });
        message.success(t('tokenDeliveries.notifications.update_success'));
      } else {
        const request: TokenDeliveryRequest = {
          token_id: values.token_id,
          delivery_id: values.delivery_id,
        };
        await tokenDeliveryService.createTokenDelivery(request);
        message.success(t('tokenDeliveries.notifications.create_success'));
      }
      onSuccess();
      onClose();
      form.resetFields();
    } catch (error) {
      console.error('Error submitting token-delivery form:', error);
      message.error(t('tokenDeliveries.notifications.error', { message: (error as Error).message }));
    } finally {
      setIsLoading(false);
    }
  };

  // Reset form when modal opens or tokenDelivery changes
  useEffect(() => {
    if (isVisible) {
      form.setFieldsValue({
        token_id: tokenDelivery?.token_id || undefined,
        delivery_id: tokenDelivery?.delivery_id || undefined,
      });
    }
  }, [form, tokenDelivery, isVisible]);

  // Filter out tokens that already have connections (except current one when editing)
  const availableTokens = tokens.filter(token => {
    if (isEditing && tokenDelivery && token.id === tokenDelivery.token_id) {
      return true; // Include current token when editing
    }
    return !existingTokenIds.includes(token.id);
  });

  // Helper function to truncate token for display
  const truncateToken = (token: string) => {
    if (token.length > 20) {
      return token.substring(0, 20) + '...';
    }
    return token;
  };

  return (
    <Modal
      title={title}
      open={isVisible}
      onCancel={onClose}
      footer={null}
      destroyOnClose={true}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
      >
        <Form.Item
          name="token_id"
          label={t('tokenDeliveries.form.token')}
          rules={[
            {
              required: true,
              message: t('tokenDeliveries.form.token_required'),
            },
          ]}
        >
          <Select
            placeholder={t('tokenDeliveries.form.token_placeholder')}
            loading={loadingData}
            disabled={isEditing} // Can't change token when editing
            showSearch
            optionFilterProp="children"
          >
            {availableTokens.map(token => (
              <Select.Option key={token.id} value={token.id}>
                {truncateToken(token.token)} {token.comment ? `(${token.comment})` : ''}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="delivery_id"
          label={t('tokenDeliveries.form.delivery')}
          rules={[
            {
              required: true,
              message: t('tokenDeliveries.form.delivery_required'),
            },
          ]}
        >
          <Select
            placeholder={t('tokenDeliveries.form.delivery_placeholder')}
            loading={loadingData}
            showSearch
            optionFilterProp="children"
          >
            {deliveries.map(delivery => (
              <Select.Option key={delivery.id} value={delivery.id}>
                {delivery.name ? `${delivery.name} (${delivery.domain})` : delivery.domain}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item>
          <Space>
            <Button
              type="primary"
              htmlType="submit"
              loading={isLoading}
            >
              {t('tokenDeliveries.form.submit')}
            </Button>
            <Button onClick={onClose}>
              {t('tokenDeliveries.form.cancel')}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default TokenDeliveryForm;
